import { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Autocomplete,
  Chip,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@apollo/client';
import { GET_ALL_LABELS } from '../graphql/getAllLabels';

interface SearchBarProps {
  onSearchResults: (query: string, selectedLabel?: string) => void;
}

const SearchBar = ({ onSearchResults }: SearchBarProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  const { data: labelsData, loading: labelsLoading } = useQuery(GET_ALL_LABELS);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    onSearchResults(query, selectedLabel || undefined);
  };

  const handleLabelChange = (event: any, newValue: string | null) => {
    setSelectedLabel(newValue);
    onSearchResults(searchQuery, newValue || undefined);
  };

  return (
    <Box sx={{ mb: 3, width: '100%', maxWidth: '800px' }}>
      <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
        <TextField
          variant="outlined"
          placeholder="Search questions..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#7f8c8d' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
              },
              '&.Mui-focused': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                boxShadow: '0 0 0 2px rgba(52, 152, 219, 0.2)',
              },
            },
            '& .MuiInputLabel-root': {
              fontWeight: '500',
              color: '#5a6c7d',
            },
          }}
        />
        <Autocomplete
          sx={{ 
            flex: 1,
            '& .MuiAutocomplete-inputRoot': {
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
              },
              '&.Mui-focused': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                boxShadow: '0 0 0 2px rgba(52, 152, 219, 0.2)',
              },
            },
          }}
          options={labelsData?.getAllLabels || []}
          value={selectedLabel}
          onChange={handleLabelChange}
          loading={labelsLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Filter by Tag"
              variant="outlined"
              placeholder="Select a tag..."
              sx={{
                '& .MuiInputLabel-root': {
                  fontWeight: '500',
                  color: '#5a6c7d',
                },
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                key={option}
                sx={{
                  borderRadius: '16px',
                  backgroundColor: 'rgba(52, 152, 219, 0.1)',
                  borderColor: 'rgba(52, 152, 219, 0.3)',
                  color: '#3498db',
                  fontWeight: '500',
                }}
              />
            ))
          }
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ padding: '12px 16px' }}>
              {option}
            </Box>
          )}
          clearOnEscape
          clearText="Clear"
          noOptionsText="No labels found"
        />
      </Stack>
    </Box>
  );
};

export default SearchBar;
